import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import Logo from "@/components/common/Logo";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (ev) => {
        ev.preventDefault();
        setError("");
        if (!email) return setError(tr.validation.required);
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError(tr.validation.emailInvalid);

        setLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setSent(true);
            toast.success(tr.forgot.successTitle);
        } catch (_e) {
            // We still show success for security (don’t leak account existence)
            setSent(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <div className="mb-8 flex justify-center">
                    <Logo variant="dark" />
                </div>

                <div className="card-elevated p-8">
                    <Link
                        to="/login"
                        data-testid="forgot-back-link"
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground mb-6"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> {tr.forgot.backToLogin}
                    </Link>

                    {!sent ? (
                        <>
                            <h1 className="text-[24px] font-bold tracking-tight">{tr.forgot.title}</h1>
                            <p className="mt-2 text-[14px] text-muted-foreground">{tr.forgot.subtitle}</p>

                            <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate data-testid="forgot-form">
                                <div className="space-y-1.5">
                                    <label htmlFor="fp-email" className="block text-[13px] font-semibold text-foreground">
                                        {tr.common.email}
                                    </label>
                                    <div className="relative">
                                        <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                        <input
                                            id="fp-email"
                                            type="email"
                                            autoComplete="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="ornek@ombagency.com"
                                            data-testid="forgot-email"
                                            className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition"
                                        />
                                    </div>
                                    {error && <p className="text-xs text-[hsl(var(--destructive))]">{error}</p>}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="forgot-submit"
                                    className="btn-brand w-full h-11 text-[14px]"
                                >
                                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                    {tr.forgot.cta}
                                </button>
                            </form>
                        </>
                    ) : (
                        <div className="text-center py-4" data-testid="forgot-success">
                            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] mb-4">
                                <CheckCircle2 className="h-8 w-8" />
                            </div>
                            <h2 className="text-[20px] font-bold tracking-tight">{tr.forgot.successTitle}</h2>
                            <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed">
                                {tr.forgot.successBody}
                            </p>
                            <Link
                                to="/login"
                                className="btn-brand mt-6 w-full h-11 text-[14px]"
                            >
                                {tr.forgot.backToLogin}
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
